import { FC, useEffect, useMemo, useState } from 'react'

// Imports diretos (e não pelos barrels): o barril de componentes arrasta o app inteiro e torna este
// bloco impossível de testar isoladamente. Mesmo padrão do `EncerramentoEsusModal` (E5c).
import { ButtonType } from '../../../../../../_metronic/helpers/enumsUI'
import { showAlert, showError } from '../../../../../../_metronic/helpers/AlertHelper'
import { Button } from '../../../../../components/Button/Button'
import { IOption } from '../../../../../components/Input/CustomSelect'
import { CustomSelect } from '../../../../../components/Input/CustomSelect'
import { Label } from '../../../../../components/Label/Label'
import { useModal } from '../../../../../components/Modal/ModalContext'
import {
  AtendimentoEsusContexto,
  EsusEncerramentoLotacaoView,
} from '../../../atendimento/core/_models'
import { getContextoEsusEncerramento } from '../../../atendimento/core/_requests'
import { corrigirLotacaoAtendimento } from '../core/_requests'

type Props = {
  atendimentoId: number
  /** Disparado após o PATCH dar certo — o detalhe encadeia o reenvio (AC3) e recarrega. */
  aoCorrigir: () => void | Promise<void>
  /** Recarrega o detalhe sem iniciar o reenvio, usado quando o PATCH encontra conflito de status. */
  aoAtualizar: () => void | Promise<void>
}

/** Mesmo rótulo do seletor de encerramento (E5c): CBO e, quando houver, o INE da equipe. */
const rotuloLotacao = (lotacao: EsusEncerramentoLotacaoView): string => {
  const cbo = lotacao.cbo
  const base = cbo
    ? [cbo.codigo, cbo.descricao].filter(Boolean).join(' - ')
    : `Lotação ${lotacao.id}`
  return lotacao.ine ? `${base} (INE ${lotacao.ine})` : base
}

const paraOpcao = (lotacao: EsusEncerramentoLotacaoView): IOption<number> => ({
  label: rotuloLotacao(lotacao),
  value: lotacao.id,
})

/**
 * Corrigir-e-reenviar (AC4): define/corrige a lotação carimbada no atendimento sem sair do detalhe
 * do envio.
 *
 * As lotações elegíveis vêm de `GET /atendimentos/{id}/esus-encerramento` (E1d), a mesma fonte do
 * seletor de encerramento — não existe segunda fonte de lotações, de propósito.
 *
 * O PATCH manda `lotacaoId` como **query param** (`@RequestParam` no backend); no corpo seria no-op
 * silencioso. Ver `corrigirLotacaoAtendimento`.
 */
export const EsusCorrigirLotacaoModal: FC<Props> = ({ atendimentoId, aoCorrigir, aoAtualizar }) => {
  const { closeModal } = useModal()

  const [contexto, setContexto] = useState<AtendimentoEsusContexto>()
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [escolhida, setEscolhida] = useState<IOption<number> | null>(null)

  useEffect(() => {
    let ativo = true
    getContextoEsusEncerramento(atendimentoId)
      .then((dados) => {
        if (!ativo) return
        setContexto(dados)
        const sugerida = dados.lotacoes.find((lotacao) => lotacao.ultimaUsada) ?? dados.lotacoes[0]
        setEscolhida(sugerida ? paraOpcao(sugerida) : null)
      })
      .catch((err) => {
        if (ativo) showError(err)
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
    }
  }, [atendimentoId])

  const opcoes = useMemo(() => (contexto?.lotacoes || []).map(paraOpcao), [contexto])

  const confirmar = async () => {
    if (!escolhida) return
    setSalvando(true)
    try {
      await corrigirLotacaoAtendimento(atendimentoId, Number(escolhida.value))
      closeModal()
      await aoCorrigir()
    } catch (err: any) {
      if (err?.response?.status === 409) {
        showAlert({
          title: 'Correção não permitida',
          text: 'O envio já foi transmitido ao PEC; a lotação não pode mais ser corrigida.',
          icon: 'info',
        })
        closeModal()
        await aoAtualizar()
      } else {
        showError(err)
      }
    } finally {
      setSalvando(false)
    }
  }

  if (carregando) {
    return <div className='p-5 text-center text-muted'>Carregando lotações...</div>
  }

  return (
    <div>
      <p className='text-muted fs-7 mb-5'>
        A lotação escolhida é carimbada no atendimento e passa a compor o cabeçalho da Ficha de
        Atendimento Individual enviada ao e-SUS APS.
      </p>

      {/*
        A integração pode ter sido desativada depois de o envio falhar. O registro em ERRO continua
        existindo, então a correção segue disponível — só avisamos que o reenvio não vai sair
        enquanto a integração estiver desligada.
      */}
      {contexto && !contexto.esusAtivo && (
        <div className='alert alert-warning'>
          A integração e-SUS APS está desativada para esta unidade. Você pode corrigir a lotação
          agora, mas o reenvio só será transmitido depois que a integração for reativada.
        </div>
      )}

      {opcoes.length === 0 ? (
        <div className='alert alert-warning'>
          Nenhuma lotação elegível foi encontrada para o profissional deste atendimento. Cadastre
          uma lotação do profissional na unidade (Cadastro do profissional → Lotações) e volte aqui.
        </div>
      ) : (
        <>
          <Label className='pb-2' isRequired>
            Lotação
          </Label>
          <CustomSelect
            options={opcoes}
            value={escolhida ?? undefined}
            placeholder='Selecione a lotação'
            onChange={(opcao: IOption<number> | null) => setEscolhida(opcao)}
          />
        </>
      )}

      <div className='d-flex flex-center flex-row-fluid pt-10'>
        <Button type={ButtonType.Reset} className='btn-light me-3' onClick={closeModal}>
          Cancelar
        </Button>
        <Button
          type={ButtonType.Button}
          className='btn-primary'
          disabled={!escolhida || salvando}
          onClick={confirmar}
        >
          Corrigir lotação
        </Button>
      </div>
    </div>
  )
}
